import React, { FC, useEffect, useState } from "react";

import { CircleLoader } from "@shared/components";
import { NumberInput } from "../NumberInput";

import { fetchPost } from "@helpers/http";

import { ResendCodeLink, ResendCodeStatus } from "../ResendCodeLink/ResendCodeLink";
import "./EnterCode.scss";

interface Props {
  isSms: boolean;
  isLogin: boolean;
  mobileNumber?: string;
  goToNextStep?: () => void;
  set2FAEnabled?: () => void;
}

const EnterCode: FC<Props> = ({ isSms, goToNextStep, set2FAEnabled, isLogin, mobileNumber }) => {
  const [codeValue, setCodeValue] = useState(["", "", "", "", "", ""]);
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCodeStatus, setResendCodeStatus] = useState(ResendCodeStatus.Normal);

  useEffect(() => {
    setIsError(false);

    if (codeValue.every(code => code !== "")) {
      handleSubmit(codeValue.join(""));
    }
  }, [codeValue]);

  useEffect(() => {
    if (isLogin) {
      resendCode();
    }
  }, []);

  const setSubmitState = () => {
    setIsSubmitting(true);
  };

  const updateResendCodeStatus = status => {
    setResendCodeStatus(status);
    if (status === ResendCodeStatus.Sent || status === ResendCodeStatus.Error) {
      setTimeout(() => {
        setResendCodeStatus(ResendCodeStatus.Normal);
      }, 3000);
    }
  };

  const showLastFourDigits = () => {
    return mobileNumber.slice(-4);
  };

  const showCorrectText = () => {
    if (isSms && isLogin) {
      return (
        <p className="code-explanation">
          You currently have two-factor verification setup on your account. A verification code was
          sent to your mobile number, ending in <strong>{showLastFourDigits()}</strong>. Please
          enter the 6 digit code below to continue.
          <ResendCodeLink onClick={resendCode} status={resendCodeStatus} />
        </p>
      );
    } else if (isSms) {
      return (
        <p className="code-explanation">
          We just sent a code to the mobile number you provided, ending in{" "}
          <strong>{showLastFourDigits()}</strong>. Please enter the 6 digit code below.
          <ResendCodeLink onClick={resendCode} status={resendCodeStatus} />
        </p>
      );
    } else if (isLogin) {
      return (
        <p className="code-explanation">
          You currently have two-factor verification set up on your account. Please enter the 6
          digit code shown by your authenticator app to continue.
        </p>
      );
    }
  };

  const getHandleSubmitPath = () => {
    if (isLogin) {
      return "/authenticate";
    }
    return "/o/two_factor";
  };

  const onSubmit = e => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(codeValue.join(""));
    return null;
  };

  const handleSubmit = (value: string) => {
    setSubmitState();
    const postPath = getHandleSubmitPath();

    fetchPost(postPath, {
      action: "create",
      token: value,
      method: isSms ? "mobile" : "qrCode",
    })
      .then(response => {
        if (response.status === 422) {
          setIsError(true);
          setIsSubmitting(false);
          return;
        }
        if (isLogin) {
          window.location.href = "/p/stores";
          return;
        }

        if (set2FAEnabled !== undefined) {
          set2FAEnabled();
        }
        if (goToNextStep !== undefined) {
          goToNextStep();
          return;
        }
      })
      .catch(() => {
        setIsSubmitting(false);
        setIsError(true);
        return;
      });
  };

  const getResendCodePath = () => {
    if (isLogin) {
      return "/authenticate/send_code";
    }
    return "/o/two_factor";
  };

  const resendCode = () => {
    const postPath = getResendCodePath();
    updateResendCodeStatus(ResendCodeStatus.Sending);

    fetchPost(postPath, {
      mobile: mobileNumber,
    })
      .then(response => {
        if (response.status === 422) {
          setIsSubmitting(false);
          updateResendCodeStatus(ResendCodeStatus.Error);
          return;
        }
        updateResendCodeStatus(ResendCodeStatus.Sent);
      })
      .catch(() => {
        setIsSubmitting(false);
        updateResendCodeStatus(ResendCodeStatus.Error);
        return;
      });
  };

  return (
    <div className="enter-code">
      {showCorrectText()}
      <div className="center-inputs">
        <NumberInput numberValue={codeValue} setNumberValue={setCodeValue} onSubmit={onSubmit} />
      </div>
      {isSubmitting && <CircleLoader />}
      {isError && (
        <div className="enter-code-error">You have entered the wrong code. Please try again.</div>
      )}
    </div>
  );
};

export { EnterCode };
